"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { HiOutlineBookmark } from "react-icons/hi";
import dynamic from "next/dynamic";
import { Button } from "@/components/ui";
import { useUser } from "@/contexts/user-provider";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import type { EmailTemplateBodyFormat } from "@/types/email-template";

// The dialog drags in Radix, the form inputs and the create mutation, none of
// which are needed to render a button nobody may click. `SaveAsTemplate` is
// registered on the shared EmailComposer compound, so a static import would
// land all of it in every composer surface's bundle — including reply, forward
// and the patient modal, which don't offer this action at all.
const SaveTemplateForm = dynamic(() => import("./save-template-form").then(m => m.SaveTemplateForm), {
  ssr: false
});
export interface SaveTemplateBody {
  /** Serialized Tiptap JSON, or raw HTML — whichever the surface authored. */
  body: string;
  bodyFormat: EmailTemplateBodyFormat;
}
interface SaveTemplateButtonProps {
  /** Read the body at click time — not at render time. */
  getTemplateBody: () => SaveTemplateBody;
  /** Prefills the dialog's name and subject fields. */
  defaultSubject?: string;
  disabled?: boolean;
}

/**
 * Keeps a composed email as a reusable template, without leaving the compose
 * surface. The return trip for the template picker: templates could only be
 * authored from scratch under Settings, so a good blast someone had already
 * written had to be written a second time to become reusable.
 *
 * Context-free like `LoadTemplateMenu` — the caller says how to serialize its
 * body, which is what lets the blast composer offer this in both rich-text and
 * HTML mode from a single mount.
 */
export function SaveTemplateButton(t0) {
  const $ = _c(11);
  const {
    getTemplateBody,
    defaultSubject,
    disabled
  } = t0;
  const {
    currentUser
  } = useUser();
  const [open, setOpen] = useState(false);
  if (!hasPermission(currentUser, PERMISSIONS.MESSAGING_EMAILS_WRITE)) {
    return null;
  }
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(true);
    t2 = <HiOutlineBookmark />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== disabled) {
    t3 = <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={t1} icon={t2} className="shrink-0 text-caption text-foreground-muted">Save as template</Button>;
    $[2] = disabled;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== defaultSubject || $[5] !== getTemplateBody || $[6] !== open) {
    t4 = open ? <SaveTemplateForm getTemplateBody={getTemplateBody} defaultSubject={defaultSubject} onClose={() => setOpen(false)} /> : null;
    $[4] = defaultSubject;
    $[5] = getTemplateBody;
    $[6] = open;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}